import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import Modal from "@/common/components/Modal";
import ResumeBox from "@/common/components/ResumeBox";
import ResumeItem from "@/common/components/ResumeItem";
import Skeleton from "@/common/components/Skeleton";
import TextArea from "@/common/components/TextArea";
import TextField from "@/common/components/TextField";
import { UnitTextField } from "@/common/components/UnitTextField";
import useDebounce from "@/common/hooks/useDebounce";
import { cn } from "@/common/utils/cn";
import { formatCurrency } from "@/common/utils/formatters";
import { useMaintenanceWorkflow } from "@/features/maintenance/features/workflows/hooks/useMaintenanceWorkflow";
import { MaintenanceEquipmentPieceData } from "@/features/maintenance/features/workflows/types/maintenanceEquipmentPiece.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { PlusIcon, SearchIcon, WrenchIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMaintenanceEquipmentPiecesInfinite } from "../../../../../../hooks/equipments/useMaintenanceEquipmentPiecesInfinite";
import { useCreateDiagnosticObjectivePiece } from "../../../../../../hooks/diagnosticObjectivePieces/useCreateDiagnosticObjectivePiece";
import { diagnosticObjectivePieceSchema, DiagnosticObjectivePieceValidationSchema } from "../../../../../../schemas/diagnosticObjectivePiece.schema";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";

interface CreateDiagnosticObjectivePieceProps {
  diagnosticObjectiveId: string;
  diagnosticObjectivePartId: string;
  selectedPiecesIds: string[];
}

const CreateDiagnosticObjectivePiece = ({
  diagnosticObjectiveId,
  diagnosticObjectivePartId,
  selectedPiecesIds,
}: CreateDiagnosticObjectivePieceProps) => {
  const { workflowId, forwardingId } = useParams();

  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [selectedPiece, setSelectedPiece] = useState<MaintenanceEquipmentPieceData>();

  const [formDisabled, setFormDisabled] = useState(false);

  const [search, setSearch] = useDebounce('');

  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

  const {
    data: maintenanceWorkflow,
  } = useMaintenanceWorkflow(workflowId);

  const {
    data: equipmentPieces,
    error: equipmentPiecesError = {} as any,
    refetch: refetchEquipmentPieces,
    hasNextPage,
    isFetchingNextPage,
    isPending: equipmentPiecesIsPending,
    fetchNextPage,
  } = useMaintenanceEquipmentPiecesInfinite<MaintenanceEquipmentPieceData[]>({
    equipmentId: maintenanceWorkflow?.equipment?.id,
    filters: {
      search,
      partId: diagnosticObjectivePartId,
    },
  });

  const {
    control,
    reset,
    handleSubmit,
    setError,
    formState: {
      errors,
    },
  } = useForm<DiagnosticObjectivePieceValidationSchema>({
    resolver: zodResolver(diagnosticObjectivePieceSchema),
    disabled: formDisabled,
  });

  const {
    unit_price: watchUnitPrice,
    quantity: watchQuantity,
  } = useWatch({
    control,
  });

  const {
    mutate: createDiagnosticObjectivePiece,
    isPending: createDiagnosticObjectivePieceIsPending,
  } = useCreateDiagnosticObjectivePiece({
    diagnosticObjectiveId,
    pieceId: selectedPiece?.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwardingId] });

        setIsOpen(false);

        setTimeout(reset, 500);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      }
    },
  });

  const onSubmit: SubmitHandler<DiagnosticObjectivePieceValidationSchema> = (data) => createDiagnosticObjectivePiece(data);

  const totalValue = useMemo(() => {
    const parsedUnitPrice = watchUnitPrice ? unFormatCurrency(watchUnitPrice) : 0;
    const parsedQuantity = watchQuantity ? unFormatUnknownUnit(watchQuantity) : 0;

    return (parsedUnitPrice * parsedQuantity) || 0;
  }, [watchUnitPrice, watchQuantity]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPiece(undefined);
      setTimeout(reset, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    setFormDisabled(createDiagnosticObjectivePieceIsPending);
  }, [createDiagnosticObjectivePieceIsPending]);

  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      if (scrollHeight - scrollTop <= clientHeight + 50) {
        if (hasNextPage && !isFetchingNextPage && !equipmentPiecesIsPending) {
          fetchNextPage();
        }
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [scrollContainer, hasNextPage, isFetchingNextPage, equipmentPiecesIsPending, fetchNextPage]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button operationKlassToCheckPermission="Establishment::Maintenance::DiagnosticObjectivePieces::Create">
            <PlusIcon size={16} />
          </Button>
        )}
        title="Adicionar Peça"
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        useCancellableFooter={!!selectedPiece}
        onCancel={(event) => {
          event.preventDefault();

          setSelectedPiece(undefined);
        }}
        onConfirm={handleSubmit(onSubmit)}
        cancelButtonText="Voltar"
        confirmButtonText="Adicionar"
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue"
        confirmButtonIsLoading={createDiagnosticObjectivePieceIsPending}
        body={
          !selectedPiece ? (
            <ErrorMessageWrapper
              statusCode={equipmentPiecesError?.status}
              refetchFunction={refetchEquipmentPieces}
              className="h-80"
            >
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                  <TextField
                    LeftIcon={<SearchIcon />}
                    placeholder="Pesquisar"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </div>

                <div
                  ref={setScrollContainer}
                  className="flex flex-col gap-2 h-96 overflow-auto"
                >
                  {Number(equipmentPieces?.length) === 0 && (
                    <EmptyDataMessage
                      className="h-96"
                    />
                  )}

                  {Number(equipmentPieces?.length) !== 0 && equipmentPieces.map((piece) => (
                    <Box
                      className={cn(
                        'bg-white-200 hover:bg-white-200/20 transition-colors cursor-pointer p-4 flex gap-4',
                        selectedPiecesIds.includes(piece.id) && 'opacity-75 text-white-400 cursor-default pointer-events-none',
                      )}
                      onClick={() => !selectedPiecesIds.includes(piece.id) && setSelectedPiece(piece)}
                    >
                      <ImageCarousel
                        files={piece.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
                        Placeholder={(
                          <ImageWithPlaceholder
                            className="size-20 rounded-md"
                            iconClassName="size-10"
                            placeholderIcon={WrenchIcon}
                          />
                        )}
                        className="size-20"
                      />

                      <div className="flex-1 flex flex-col gap-2">
                        <div className="flex items-start justify-between">
                          <p className="text-base font-bold">
                            {piece?.display_name || '--'}
                          </p>

                          {selectedPiecesIds.includes(piece.id) && (
                            <p className="text-xs italic">
                              Já está em uso
                            </p>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <ResumeItem
                            className="text-xs"
                            label="Código da peça"
                            value={piece?.number || '--'}
                          />

                          <ResumeItem
                            className="text-xs"
                            label="Fabricante"
                            value={piece?.brand?.display_name || '--'}
                          />
                        </div>
                      </div>
                    </Box>
                  ))}

                  {isFetchingNextPage && (
                    <Skeleton className="min-w-full min-h-20 rounded-sm" />
                  )}
                </div>
              </div>
            </ErrorMessageWrapper>
          ) : (
            <div className="flex flex-col gap-4">
              <Box className="bg-white-200 p-4 flex gap-4">
                <ImageCarousel
                  files={selectedPiece.images?.map((image) => ({ id: image?.url, file: image?.url })) || []}
                  Placeholder={(
                    <ImageWithPlaceholder
                      className="size-20 rounded-md"
                      iconClassName="size-10"
                      placeholderIcon={WrenchIcon}
                    />
                  )}
                  className="size-20"
                />

                <div className="flex flex-col gap-2">
                  <p className="text-base font-bold">
                    {selectedPiece?.display_name || '--'}
                  </p>

                  <div className="flex gap-3">
                    <ResumeItem
                      className="text-xs"
                      label="Código da peça"
                      value={selectedPiece?.number || '--'}
                    />

                    <ResumeItem
                      className="text-xs"
                      label="Fabricante"
                      value={selectedPiece?.brand?.display_name || '--'}
                    />
                  </div>
                </div>
              </Box>

              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => (
                    <TextArea
                      label="Razão pela troca de peças"
                      rows={4}
                      {...field}
                      errorMessage={errors.description?.message}
                    />
                  )}
                />

                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Controller
                      control={control}
                      name="unit_price"
                      render={({ field }) => (
                        <CurrencyTextField
                          label="Preço unitário"
                          markAsRequired
                          {...field}
                          errorMessage={errors.unit_price?.message}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="quantity"
                      render={({ field }) => (
                        <UnitTextField
                          label="Quantidade"
                          markAsRequired
                          decimals={0}
                          {...field}
                          errorMessage={errors.quantity?.message}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name="warranty_in_days"
                      render={({ field }) => (
                        <UnitTextField
                          label="Garantia"
                          markAsRequired
                          unit="Dias"
                          decimals={0}
                          {...field}
                          errorMessage={errors.warranty_in_days?.message}
                        />
                      )}
                    />
                  </div>

                  <ResumeBox
                    label="Valor total"
                    value={formatCurrency(totalValue)}
                    padding="small"
                    boxClassName="bg-white-200 rounded-md"
                  />
                </div>
              </div>
            </div>
          )
        }
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default CreateDiagnosticObjectivePiece;
